import React from "react";
import { validatePassword, getStrengthColor } from "./PasswordUtil";
import "./css/PasswordStrengthMeter.css";

/**
 * PasswordStrengthMeter Component
 *
 * Displays real-time password strength as the user types.
 * Uses the existing validatePassword function from PasswordUtil.
 *
 * @param {string} password - The password to evaluate
 * @param {boolean} showMeter - Whether to display the meter (default: true)
 */
function PasswordStrengthMeter({ password, showMeter = true }) {
  // Don't show anything if password is empty or showMeter is false
  if (!password || !showMeter) {
    return null;
  }

  const validation = validatePassword(password);
  const { strength, message } = validation;

  // Get the color for the strength level
  const strengthColor = getStrengthColor(strength);

  // Calculate the width of the strength bar based on strength level
  const getStrengthWidth = (strengthLevel) => {
    switch (strengthLevel) {
      case "weak":
        return "25%";
      case "good":
        return "50%";
      case "strong":
        return "75%";
      case "excellent":
        return "100%";
      default:
        return "0%";
    }
  };

  // Get a user-friendly label for the strength
  const getStrengthLabel = (strengthLevel) => {
    switch (strengthLevel) {
      case "weak":
        return "Weak";
      case "good":
        return "Good";
      case "strong":
        return "Strong";
      case "excellent":
        return "Excellent";
      default:
        return "";
    }
  };

  const strengthWidth = getStrengthWidth(strength);
  const strengthLabel = getStrengthLabel(strength);

  return (
    <div className="password-strength-meter">
      <div className="strength-bar-container">
        <div
          className="strength-bar"
          style={{
            width: strengthWidth,
            backgroundColor: strengthColor,
            transition: "width 0.3s ease, background-color 0.3s ease",
          }}
        />
      </div>
      <div className="strength-info">
        <span
          className="strength-label"
          style={{ color: strengthColor, fontWeight: "600" }}
        >
          {strengthLabel}
        </span>
        {!validation.isValid && message && (
          <span className="strength-message">{message}</span>
        )}
      </div>
    </div>
  );
}

export default PasswordStrengthMeter;
